import {DocumentPartTemplateData, Template, TemplateType, TemplateItemParams} from '@dv5/domain'
import React, {FC, useState} from 'react'
import {DeleteButton, EditButton, ViewButton, Icon, IconName} from "../buttons"
import {Input} from "../inputs/Input"
import './TemplateItem.less'

interface TemplateItemProps {
    template: Template
    mode?: 'selection' | 'manager'
    onSelect: (template: Template, params: TemplateItemParams) => void
    onDelete: (templateId: string) => void
    onRename: (templateId: string, newTitle: string) => void
    onViewDetails?: (template: Template) => void
    isSelected?: boolean
}

export const TemplateItem: FC<TemplateItemProps> = ({
                                                        template,
                                                        mode,
                                                        onSelect,
                                                        onDelete,
                                                        onRename,
                                                        onViewDetails,
                                                        isSelected = false,
                                                    }) => {
    const [isEditing, setIsEditing] = useState(false)
    const [editedTitle, setEditedTitle] = useState(template.title)

    const handleClick = () => {
        if (mode === 'selection') {
            onSelect(template, {})
            return
        }
        if (!isEditing && mode === 'manager') {
            setIsEditing(true)
            setEditedTitle(template.title)
        }
    }

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        onDelete(template.id)
    }

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        setIsEditing(true)
        setEditedTitle(template.title)
    }

    const handleConfirm = () => {
        if (editedTitle.trim() && editedTitle !== template.title) {
            onRename(template.id, editedTitle.trim())
        }
        setIsEditing(false)
    }

    const handleCancel = () => {
        setIsEditing(false)
        setEditedTitle(template.title)
    }

    const handleSelectClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        onSelect(template, {})
    }

    return (
        <div className={`template-item${isSelected ? ' selected' : ''}`}
             id={`template-item-${template.id}`}>
            <div className="template-content" onClick={handleClick}>
                {isEditing ? (
                    <Input
                        id={`template-edit-${template.id}`}
                        label=""
                        value={editedTitle}
                        autoFocus
                        autoSelect
                        saveOnBlur
                        placeholder="Template name"
                        withoutHeader={true}
                        setValue={setEditedTitle}
                        onConfirm={handleConfirm}
                        onCancel={handleCancel}
                    />
                ) : (
                    <>
                        <div className="template-title-row">
                            <div className="template-title">{template.title}</div>
                            {template.settings &&
                                <Icon name={IconName.Settings} className="template-settings-icon" width="16"
                                      height="16"/>
                            }
                        </div>
                        {template.templateType === TemplateType.DOCUMENT_PART &&
                            <div
                                className="template-part-type">{(template.templateData as DocumentPartTemplateData).partType}</div>
                        }
                    </>
                )}
            </div>
            <div className="template-actions">
                {!isEditing && mode !== 'manager' && (
                    <button
                        className="template-select-button"
                        onClick={handleSelectClick}
                        aria-label="Select"
                    >
                        <Icon
                            name={IconName.Check}
                            className="select-icon"
                            width="20"
                            height="20"
                        />
                    </button>
                )}
                {!isEditing && mode === 'manager' && onViewDetails && (
                    <div onClick={(e) => e.stopPropagation()}>
                        <ViewButton
                            tooltipText={'View/edit data'}
                            onView={() => onViewDetails(template)}
                            className="template-view-button"
                        />
                    </div>
                )}
                {!isEditing && mode === 'manager' && (
                    <div onClick={handleEditClick}>
                        <EditButton
                            tooltipText={'Rename template'}
                            onEdit={() => {
                                setIsEditing(true)
                                setEditedTitle(template.title)
                            }}
                            className="template-edit-button"
                        />
                    </div>
                )}
                {!isEditing && mode === 'manager' &&
                    <div>
                        <DeleteButton
                            onDelete={() => onDelete(template.id)}
                            className="template-delete-button"
                        />
                    </div>
                }
            </div>
        </div>
    )
}
